import { useState } from "react";
import { ChevronDown } from "lucide-react";

import { Callout } from "@/components/ui/Display.jsx";
import { TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import { cx } from "@/lib/cx.js";

/*
 * The docs renderer: one component per block type from src/content/docs.js.
 *
 * NOTHING HERE USES dangerouslySetInnerHTML. The content is ours and would be
 * safe enough, but a docs page that renders raw HTML is one paste away from
 * being an XSS hole the day someone loads a topic from the API instead. Inline
 * emphasis is a marker in the text and is parsed below.
 */

/* ---- Inline: **bold**, _italic_, `code` --------------------------------- */

const INLINE = /(\*\*[^*]+\*\*|_[^_]+_|`[^`]+`)/g;

export function Inline({ text }) {
    if (!text) return null;

    return (
        <>
            {String(text)
                .split(INLINE)
                .filter(Boolean)
                .map((part, i) => {
                    if (part.startsWith("**") && part.endsWith("**")) {
                        return (
                            <strong key={i} className="font-semibold">
                                {part.slice(2, -2)}
                            </strong>
                        );
                    }
                    if (part.startsWith("_") && part.endsWith("_")) {
                        return <em key={i}>{part.slice(1, -1)}</em>;
                    }
                    if (part.startsWith("`") && part.endsWith("`")) {
                        return (
                            <code
                                key={i}
                                className="rounded bg-slate-100 px-1 py-px font-mono text-[12px] dark:bg-slate-800"
                            >
                                {part.slice(1, -1)}
                            </code>
                        );
                    }
                    return <span key={i}>{part}</span>;
                })}
        </>
    );
}

/* ---- Blocks -------------------------------------------------------------- */

export function Blocks({ blocks = [], className }) {
    return (
        <div className={cx("space-y-4", className)}>
            {blocks.map((block, i) => (
                <Block key={i} block={block} />
            ))}
        </div>
    );
}

function Block({ block }) {
    switch (block.type) {
        case "p":
            return (
                <p className="text-[13.5px] leading-relaxed text-slate-600 dark:text-slate-300">
                    <Inline text={block.text} />
                </p>
            );

        case "steps":
            return (
                <div>
                    {block.title && <Heading>{block.title}</Heading>}
                    <ol className="space-y-2.5">
                        {block.items.map((item, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-50 text-[11.5px] font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-200">
                                    {i + 1}
                                </span>
                                <span className="min-w-0 pt-0.5 text-[13.5px] leading-relaxed text-slate-600 dark:text-slate-300">
                                    <Inline text={typeof item === "string" ? item : item.text} />
                                </span>
                            </li>
                        ))}
                    </ol>
                </div>
            );

        case "list":
            return (
                <div>
                    {block.title && <Heading>{block.title}</Heading>}
                    <ul className="ml-4 list-disc space-y-1.5">
                        {block.items.map((item, i) => (
                            <li
                                key={i}
                                className="text-[13.5px] leading-relaxed text-slate-600 dark:text-slate-300"
                            >
                                <Inline text={item} />
                            </li>
                        ))}
                    </ul>
                </div>
            );

        case "note":
            return (
                <Callout tone={block.tone ?? "info"} data-note={block.tone ?? "info"}>
                    <p className="text-[13px] leading-relaxed">
                        {block.title && <strong className="font-semibold">{block.title} — </strong>}
                        <Inline text={block.text} />
                    </p>
                </Callout>
            );

        case "table":
            return (
                <TableWrap className="rounded-lg border border-slate-200 dark:border-slate-800">
                    <thead>
                        <tr>
                            {block.columns.map((c) => (
                                <Th key={c} className="pl-4">
                                    {c}
                                </Th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {block.rows.map((row, i) => (
                            <Tr key={i}>
                                {row.map((cell, j) => (
                                    <Td key={j} className="pl-4 align-top text-[13px]">
                                        <Inline text={cell} />
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </tbody>
                </TableWrap>
            );

        case "qa":
            return (
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                    {block.items.map((item, i) => (
                        <Question key={i} item={item} />
                    ))}
                </div>
            );

        default:
            return null;
    }
}

function Heading({ children }) {
    return <h4 className="mb-2.5 text-[14px] font-semibold">{children}</h4>;
}

/*
 * A <details> element, not a state-driven accordion.
 *
 * The browser gives open/close, keyboard operation and — the reason that
 * matters here — in-page find. Ctrl+F for a phrase inside a collapsed answer
 * scrolls to it and opens the section; a div that unmounts its content when
 * closed hides it from the browser's own search, which on a page of questions
 * is exactly the wrong thing to break.
 */
function Question({ item }) {
    const [open, setOpen] = useState(false);

    return (
        <details
            className="group"
            data-question
            onToggle={(e) => setOpen(e.currentTarget.open)}
        >
            <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 text-[13.5px] font-medium transition hover:bg-slate-50 dark:hover:bg-slate-800/60">
                <ChevronDown
                    className={cx(
                        "size-4 shrink-0 text-slate-400 transition-transform",
                        open && "rotate-180",
                    )}
                    strokeWidth={2}
                />
                <span className="min-w-0">{item.q}</span>
            </summary>
            <div className="px-4 pb-4 pl-11">
                <Blocks blocks={item.blocks} className="space-y-2.5" />
            </div>
        </details>
    );
}
